import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, List, ListItem, ListItemText, Box } from '@mui/material';

const InfoDialog = ({ open, onClose }) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Forecast Import Guide</DialogTitle>
            <DialogContent>
                <Typography variant="body1" paragraph>
                    This functionality allows you to import forecasts from a CSV file. Here&apos;s how to use it:
                </Typography>
                <List>
                    <ListItem>
                        <ListItemText
                            primary="1. Prepare your CSV file"
                            secondary={
                                <Box component="span">
                                    <Typography variant="body2" component="div">
                                        Ensure your CSV file contains the correct columns and data format as specified in the template.
                                    </Typography>
                                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                        The columns are:
                                    </Typography>
                                    <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2 }}>
                                        <li><Typography variant="body2">Product - optional (for your reference)</Typography></li>
                                        <li><Typography variant="body2">Distributor - the 6-character SAP ID of the distributor, e.g. &apos;001OBC&apos;</Typography></li>
                                        <li><Typography variant="body2">BBC SKU - the 6-character SKU of the product, e.g. &apos;OA6012&apos;</Typography></li>
                                        <li><Typography variant="body2">Customer - optional (for your reference)</Typography></li>
                                        <li><Typography variant="body2">Week columns - 8 date columns (week ending dates), one per week</Typography></li>
                                    </Box>
                                    <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                                        Additional notes:
                                    </Typography>
                                    <Box component="ul" sx={{ mt: 0.5, mb: 1, pl: 2 }}>
                                        <li><Typography variant="body2">The file can be edited in any text editor or Excel</Typography></li>
                                        <li><Typography variant="body2">The headers should be in the first row of the file and match exactly the columns specified above (case-insensitive)</Typography></li>
                                        <li><Typography variant="body2">The headers and values are automatically trimmed of whitespace</Typography></li>
                                        <li><Typography variant="body2">Distributor and SKU must be exactly 6 characters</Typography></li>
                                        <li><Typography variant="body2">Week must be a valid date that corresponds to a week in the system</Typography></li>
                                        <li><Typography variant="body2">Week must be in the future (cannot be in the past or current week)</Typography></li>
                                        <li><Typography variant="body2">The current week cannot be edited via CSV upload - forecasts must start from the next week onwards</Typography></li>
                                        <li><Typography variant="body2">Enter forecast amounts in the week columns; empty cells are ignored</Typography></li>
                                        <li><Typography variant="body2">Valid values will be imported; invalid values will be skipped and listed in the result message after upload</Typography></li>
                                    </Box>
                                </Box>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary="2. Upload the file"
                            secondary="Click the &apos;Upload Forecasts&apos; button and select your prepared CSV file."
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemText
                            primary="3. Review and confirm"
                            secondary="After selecting the file, click &apos;Upload&apos; to process the import."
                        />
                    </ListItem>
                </List>
                <Typography variant="body2" color="text.secondary">
                    Note: The system will validate your data and show any errors that need to be corrected.
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

InfoDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
};

export default InfoDialog;

